import {
  FP_ORG_LOGO_ASSET_ID,
  FP_TABLE_REPEAT_HEADER_ATTRIBUTE,
} from "@forgepoint/shared";
import { ChevronDown, Columns3, Images, Settings2, Unlink } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { AddTableColumnSelect } from "./AddTableColumnSelect";
import { FieldSourceSelect } from "./FieldSourceSelect";
import { FpImageAssetName } from "./FpImageAssetName";
import { FpImageUpload } from "./FpImageUpload";
import { FpPropertiesPanel } from "./FpPropertiesPanel";
import { listTableColumns } from "./documentEditor";
import { readFpPropValues } from "./fpPropSchema";
import { ItemNumberingSelect } from "./ItemNumberingSelect";
import { SIGNATURE_FONT_CLASS } from "./quoteFields";
import { ShowWhenSelect } from "./ShowWhenSelect";
import { SkipPageChromeToggle } from "./SkipPageChromeToggle";
import { StyleTextInput } from "./StyleTextInput";
import { TableColumnsPanel } from "./TableColumnsPanel";
import { TablePropertiesPanel } from "./TablePropertiesPanel";
import { TEXT_SESSION_HOLD_PROPS } from "./textSessionFocus";
import type { DocumentEditorApi } from "./useDocumentEditor";

interface BlockSettingsPopoverProps {
  /** The editor whose selected block the popover configures. */
  editor: DocumentEditorApi;
  /** Blocks editing (an image upload is in flight). */
  disabled: boolean;
  /** Registers a freshly uploaded asset id and its file for instant preview. */
  onLocalAsset: (assetId: string, file: File) => void;
  /** Reports whether an image upload is in flight. */
  onUploadingChange: (isUploading: boolean) => void;
}

/**
 * The toolbar's "Properties" popover: everything about the selected block that
 * is not a style knob. Visibility (show-when and the page region's
 * header/footer skips) is hand-written; every primitive's own props come from
 * the fp prop schema, so a new primitive lands here by declaring a schema
 * entry rather than by editing this file. The props the generator cannot
 * express - the field source, the authored signature font, the picture asset
 * and its pixel size, and the line custom field driving item numbers - are
 * declared as overrides and rendered below.
 *
 * The two table sections are complementary and never both apply: a hand
 * authored table gets the row and column operations, while a table bound to
 * the quote's lines - or one of its columns - gets the column picker that binds
 * one more line field.
 * The trigger disables only when nothing is selected: an open text session's
 * block is still the selection, so the popover opens on the first click and
 * holds the session (textSessionFocus.ts) instead of blurring it. Attribute
 * edits re-render the preview under the session's overlay, so every mutation
 * commits the session first.
 * @param editor - The editor whose selected block the popover configures
 * @param disabled - Blocks editing
 * @param onLocalAsset - Registers an uploaded asset id and file for preview
 * @param onUploadingChange - Reports whether an image upload is in flight
 * @returns The block settings popover
 */
export function BlockSettingsPopover({
  editor,
  disabled,
  onLocalAsset,
  onUploadingChange,
}: BlockSettingsPopoverProps) {
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  const block = editor.selection;
  const propValues: Record<string, string> =
    block === null ? {} : readFpPropValues(editor.documentHtml, block.nodeId);
  const boundSource = propValues.source === "" ? null : propValues.source;
  const bindsField =
    block !== null &&
    (block.tagName === "fp-field" ||
      block.tagName === "fp-text" ||
      block.tagName === "fp-column" ||
      (block.tagName === "fp-markdown" && block.fieldSource !== null));
  const numbersLines =
    block !== null &&
    (block.tagName === "fp-lines" ||
      (block.tagName === "fp-table" && block.table === null));
  const tableColumns =
    block === null ? [] : listTableColumns(editor.documentHtml, block.nodeId);

  /**
   * Wraps a document mutation so the open text session commits before the
   * rendered document is replaced under its overlay.
   * @param mutate - The document mutation to run after the commit
   * @returns The wrapped mutation
   */
  const commitFirst =
    <Args extends unknown[]>(mutate: (...args: Args) => void) =>
    (...args: Args): void => {
      editor.commitTextEdit();
      mutate(...args);
    };

  const setAttribute = commitFirst(editor.setAttribute);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          ref={setTrigger}
          variant="ghost"
          size="sm"
          className="shrink-0"
          disabled={disabled || block === null}
          onMouseDown={(event) => event.preventDefault()}
        >
          <Settings2 className="size-4" />
          Properties
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      {block !== null && (
        <PopoverContent
          {...TEXT_SESSION_HOLD_PROPS}
          align="start"
          container={
            trigger?.closest<HTMLElement>('[role="dialog"]') ?? undefined
          }
          className="w-72 overflow-hidden p-0"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {/*
            The scroller is this inner box, not PopoverContent. Radix positions
            and zoom-animates the content element itself, and a scroll container
            that is also being transformed does not reliably scroll. Capping on
            the Radix available-height variable as well as the viewport keeps the
            panel inside the gap between the trigger and the screen edge, which a
            bare vh cap does not. It portals into the editor's dialog rather
            than the body: the editor opens as a modal, whose scroll lock
            cancels wheel events outside the dialog's subtree, which would
            leave the panel scrollable only by dragging its scrollbar.
          */}
          <div className="max-h-[min(70vh,var(--radix-popover-content-available-height))] space-y-4 overflow-y-auto p-4">
            <div>
              <p className="text-sm font-medium" title={block.label}>
                {block.label}
              </p>
              {block.isRepeatableBlock && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {block.tagName === "header" || block.tagName === "fp-header"
                    ? "This header repeats at the top of every page."
                    : "This footer repeats at the bottom of every page."}
                </p>
              )}
              {block.hasEditableText && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Double-click the text in the preview to edit it.
                </p>
              )}
            </div>

            <ShowWhenSelect
              key={`${block.nodeId}:show-when:${block.showWhen ?? ""}`}
              showWhen={block.showWhen}
              onAttributeChange={setAttribute}
            />

            {block.opensPageRegion && (
              <>
                <SkipPageChromeToggle
                  target="header"
                  checked={block.skipsHeader}
                  onAttributeChange={setAttribute}
                />
                <SkipPageChromeToggle
                  target="firstHeader"
                  checked={block.skipsFirstHeader}
                  disabled={block.skipsHeader}
                  onAttributeChange={setAttribute}
                />
                <SkipPageChromeToggle
                  target="footer"
                  checked={block.skipsFooter}
                  onAttributeChange={setAttribute}
                />
              </>
            )}

            {!block.opensPageRegion &&
              !block.isRepeatableBlock &&
              block.ancestors.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  To hide the header or footer for this section, select the page
                  break above this section.
                </p>
              )}

            {bindsField && (
              <FieldSourceSelect
                value={
                  block.tagName === "fp-field" ||
                  block.tagName === "fp-markdown"
                    ? block.fieldSource
                    : boundSource
                }
                scope={block.tagName === "fp-column" ? "line" : "quote"}
                clearable={block.tagName === "fp-text"}
                onChange={(source) => setAttribute("source", source)}
              />
            )}

            {(block.fieldSource === "sales-rep-name" ||
              block.usesSignatureFont) && (
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <Label
                    htmlFor="signature-font"
                    className="text-[11px] font-normal"
                  >
                    Signature font
                  </Label>
                  <Switch
                    id="signature-font"
                    checked={block.usesSignatureFont}
                    onCheckedChange={(checked) =>
                      commitFirst(editor.setClass)(
                        SIGNATURE_FONT_CLASS,
                        checked,
                      )
                    }
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Renders this text in a cursive signature style. This is your
                  authored signature, separate from the customer's e-signature.
                </p>
              </div>
            )}

            {block.tagName === "fp-markdown" && block.fieldSource !== null && (
              <div className="space-y-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  title="Copy the current field text into the block and edit it freely"
                  onClick={editor.convertSelectedMarkdownToAuthored}
                >
                  <Unlink className="size-4" /> Convert to editable text
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  Copies the field's current text into this block so you can
                  double-click and edit it in place. This detaches it from the
                  quote field - future quote changes won't flow through.
                </p>
              </div>
            )}

            {block.tagName === "fp-image" && (
              <div className="space-y-2">
                <FpImageUpload
                  onLocalAsset={onLocalAsset}
                  onUploadingChange={onUploadingChange}
                  onAssetUploaded={(assetId) =>
                    setAttribute("asset-id", assetId)
                  }
                />
                {block.imageAssetId !== FP_ORG_LOGO_ASSET_ID && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    title="Bind this image to the organization's configured logo"
                    onClick={() =>
                      setAttribute("asset-id", FP_ORG_LOGO_ASSET_ID)
                    }
                  >
                    <Images className="size-4" /> Use organization logo
                  </Button>
                )}
                <FpImageAssetName assetId={block.imageAssetId} />
                <div className="grid grid-cols-2 gap-2">
                  <StyleTextInput
                    key={`${block.nodeId}:width:${block.imageWidth ?? ""}`}
                    label="Width (px)"
                    value={block.imageWidth ?? ""}
                    placeholder="auto"
                    unitless
                    onCommit={(value) =>
                      setAttribute("width", value.replace(/\D/g, ""))
                    }
                  />
                  <StyleTextInput
                    key={`${block.nodeId}:height:${block.imageHeight ?? ""}`}
                    label="Height (px)"
                    value={block.imageHeight ?? ""}
                    placeholder="auto"
                    unitless
                    onCommit={(value) =>
                      setAttribute("height", value.replace(/\D/g, ""))
                    }
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Leave one blank to scale on the image's aspect ratio.
                </p>
              </div>
            )}

            {block.columns !== null && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                title="Add a column to the row"
                onClick={commitFirst(editor.addColumnsColumn)}
              >
                <Columns3 className="size-4" /> Add column
              </Button>
            )}

            {block.table !== null && (
              <TablePropertiesPanel
                table={block.table}
                onInsertRow={commitFirst(editor.insertTableRow)}
                onRemoveRow={commitFirst(editor.removeTableRow)}
                onInsertColumn={commitFirst(editor.insertTableColumn)}
                onRemoveColumn={commitFirst(editor.removeTableColumn)}
                onMergeCells={commitFirst(editor.mergeTableCells)}
                onUnmergeCells={commitFirst(editor.unmergeTableCells)}
              />
            )}

            {tableColumns.length > 1 && (
              <TableColumnsPanel
                columns={tableColumns}
                onMove={commitFirst(editor.moveTableColumn)}
              />
            )}

            {((block.tagName === "fp-table" && block.table === null) ||
              block.tagName === "fp-column") && (
              <AddTableColumnSelect
                onAdd={commitFirst(editor.addTableColumn)}
              />
            )}

            {numbersLines && (
              <ItemNumberingSelect
                key={`${block.nodeId}:number-source:${propValues["number-source"] ?? ""}`}
                value={propValues["number-source"] ?? ""}
                onChange={(numberSource) =>
                  editor.setAttribute("number-source", numberSource)
                }
              />
            )}

            <FpPropertiesPanel
              tagName={block.tagName}
              nodeId={block.nodeId}
              values={propValues}
              previewRef={editor.previewRef}
              onAttributeChange={setAttribute}
              omit={[
                ...(block.table === null
                  ? []
                  : [FP_TABLE_REPEAT_HEADER_ATTRIBUTE]),
                ...(block.rendersAsTableCell ? ["margin"] : []),
              ]}
            />
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
